import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '../ui/Modal.jsx';
import Skeleton from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState.jsx';
import { UserRound } from 'lucide-react';
import ProfileCardV2 from '../ProfileCardV2';
import { useAuth } from '../../contexts/AuthContext';
import { authorizedFetch } from '../../utils/api';
import { normalizeProfile } from '../../utils/profileNormalize';

/**
 * Eine Profilkarte als Blatt (Okt 2026, „App wie Spot“).
 *
 * Wer im Logbuch auf eine Lernpartnerin tippt, mit der noch kein Chat besteht,
 * oder in der Suche auf eine Trefferzeile, sieht die Karte, ohne die Seite zu
 * verlassen — Scrollstand und Zusammenhang bleiben. Vernetzen geht von dort.
 *
 * - userId:  laedt das Profil ueber GET /api/v1/profile/user/:id
 * - profile: bereits geladenes Profil (Suche), dann ohne Abruf
 * - origin:  aus welcher Ansicht das Blatt geoeffnet wurde; geht an „Vernetzen“
 *            weiter (Forschung: Herkunft von Vernetzungen). Ohne Angabe
 *            PROFILE_LINK (z. B. Logbuch), die Suche setzt SEARCH, die
 *            Skill-Übersicht SKILL_BROWSE.
 * - hideConnectButton: im Chat (Kopf antippen) ist man schon vernetzt — dort
 *            fuehrte „Zum Chat“ nur in den Chat zurueck, aus dem man kommt.
 *
 * Schliessen (Oktober 2026): Auf dem Telefon nach unten wischen (Haptik an der
 * Schwelle) oder den Schleier antippen. Das X der Modal-Kopfzeile ist
 * dort weg — es sass direkt ueber dem Listen-Knopf der Karte, zwei runde Knoepfe
 * uebereinander in derselben Ecke (Heikos Screenshot). Am Desktop bleibt es.
 */
const ProfileSheet = ({
  open, onClose, userId, profile: givenProfile, name, searchTerm, onBlocked, origin = 'PROFILE_LINK',
  hideConnectButton = false,
}) => {
  const { user: currentUser } = useAuth();
  const [state, setState] = useState({ status: 'idle', profile: null });

  useEffect(() => {
    if (!open) return undefined;
    if (givenProfile) {
      setState({ status: 'ready', profile: normalizeProfile(givenProfile) });
      return undefined;
    }
    if (!userId) return undefined;
    let cancelled = false;
    setState({ status: 'loading', profile: null });
    authorizedFetch(`/api/v1/profile/user/${userId}`)
      .then(res => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        setState(data
          ? { status: 'ready', profile: normalizeProfile({ ...data, id: data.userId || userId }) }
          : { status: 'error', profile: null });
      })
      .catch(() => { if (!cancelled) setState({ status: 'error', profile: null }); });
    return () => { cancelled = true; };
  }, [open, userId, givenProfile]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel={name ? `Profil von ${name}` : 'Profil'}
      size="md"
      bodyClassName="p-0"
      swipeToClose
    >
      {/* Telefon: unter dem Blatt nur Greifer (~14px) und Safe-Area, keine Kopfzeile
          mehr — die Karte bekommt den Platz. Passt sie ganz hinein, scrollt der
          Bereich nicht, und die Wischgeste greift ueberall auf der Karte. */}
      <div className="h-[min(600px,calc(75vh_-_20px_-_var(--safe-area-inset-bottom,0px)))] px-3 pb-3 pt-2 sm:h-[min(600px,calc(75vh-64px))]">
        {state.status === 'ready' && state.profile ? (
          <ProfileCardV2
            profile={state.profile}
            currentUser={currentUser}
            searchTerm={searchTerm}
            hideMatchScore={!state.profile.matchScore}
            onBlocked={onBlocked}
            origin={origin}
            hideConnectButton={hideConnectButton}
            fill
          />
        ) : state.status === 'error' ? (
          <EmptyState icon={UserRound} title="Profil nicht verfügbar" text="Dieses Profil kann gerade nicht geladen werden." />
        ) : (
          <div className="space-y-3 p-4" aria-busy="true">
            <span className="sr-only">Profil wird geladen...</span>
            <Skeleton.Card />
          </div>
        )}
      </div>
    </Modal>
  );
};

ProfileSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string,
  profile: PropTypes.object,
  name: PropTypes.string,
  /** hebt passende Skills hervor (Suche) */
  searchTerm: PropTypes.string,
  /** Nach dem Blockieren: die aufrufende Liste nimmt die Person heraus. */
  onBlocked: PropTypes.func,
  /** Herkunft einer Vernetzungsanfrage aus diesem Blatt */
  origin: PropTypes.oneOf(['ROUND', 'SEARCH', 'SKILL_BROWSE', 'PROFILE_LINK', 'OTHER']),
  /** Vernetzen-/Zum-Chat-Knopf weglassen (Chat-Kopf) */
  hideConnectButton: PropTypes.bool,
};

export default ProfileSheet;
